import { Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";

import {
    AbstractProductVariant,
    Product,
    ProductTemplateType,
} from "@hermes/api-model-product";

import {
    FeatureFlagFacade,
    PRODUCT_PAGE_REVAMP,
    PRODUCT_PAGE_SELECTOR_REVAMP,
} from "@hermes/states/flipper";

import {
    cleanProductState,
    defaultImageGalleryPosition,
    fetchCrossSellProduct,
    fetchProduct,
    goBack,
    openSelectorToggle,
    ProductVariantType,
    resetSizeSelectedVariants,
    resetVariant,
    resetVariantSelectedBoxType,
    selectVariant,
    sendPageViewAnalytics,
    showMoreVariant,
    showMoreVariantsRequested,
    toggleZoom,
    ToggleZoomEvent,
    updateProductHeadTag,
} from "../actions/product-page.actions";
import {
    selectAPWskus,
    selectBikiniHasVariants,
    selectCrossSell,
    selectCurrentProduct,
    selectCurrentProductReference,
    selectIsLoadingProduct,
    selectSelectedVariant,
    selectTemplateType,
    selectTopLayerVariants,
    selectVariantChangeType,
    selectVariants,
    selectVariantSelectedBoxType,
    selectZoomStatus,
} from "../selectors/product.selectors";

import {
    SelectedVariantVariantsState,
    TopLayerVariants,
} from "./../types/product-page.types";

@Injectable()
export class ProductPageFacade {
    public currentProduct$ = this.store.select(selectCurrentProduct);
    public aPWskus$ = this.store.select(selectAPWskus);
    public bikiniHasVariants$ = this.store.select(selectBikiniHasVariants);
    public variantChangeType$ = this.store.select(selectVariantChangeType);
    public variants$ = this.store.select(selectVariants);
    public variantSelectedBoxType$ = this.store.select(
        selectVariantSelectedBoxType,
    );

    public currentProductReference$ = this.store.select(
        selectCurrentProductReference,
    );

    public isProductLoading$ = this.store.select(selectIsLoadingProduct);
    public crossSell$ = this.store.select(selectCrossSell);
    public zoomStatus$ = this.store.select(selectZoomStatus);
    public templateType$ = this.store.select(selectTemplateType);
    public topSelectorLayerContent = this.store.selectSignal(
        selectTopLayerVariants,
    );

    private readonly isProductPageRevampEnabledSignal: Signal<boolean> =
        toSignal(this.featureFlagFacade.isActivated(PRODUCT_PAGE_REVAMP), {
            initialValue: false,
        });

    private readonly isProductPageSelectorRevampEnabledSignal: Signal<boolean> =
        toSignal(
            this.featureFlagFacade.isActivated(PRODUCT_PAGE_SELECTOR_REVAMP),
            {
                initialValue: false,
            },
        );

    constructor(
        private store: Store,
        private featureFlagFacade: FeatureFlagFacade,
    ) {}

    public get isProductPageRevampEnabled(): boolean {
        return this.isProductPageRevampEnabledSignal();
    }

    public get isProductPageSelectorRevampEnabled(): boolean {
        return this.isProductPageSelectorRevampEnabledSignal();
    }

    /**
     * Determines whether the product selector should use the revamped version,
     * based on the product's template type and a feature flag.
     * @param productInput The product of a product to evaluate
     */
    public isProductRevampSelectorEnabled(productInput: Product): boolean {
        const revampedProductList: ProductTemplateType[] = [
            ProductTemplateType.Beltkit,
        ];
        // TODO Fill the list of products whose selectors have been revamped.

        return (
            this.isProductPageSelectorRevampEnabled &&
            revampedProductList.includes(
                productInput.templateType as ProductTemplateType,
            )
        );
    }

    /**
     * Send openSelectorToggle action
     *
     * @param toggleKey key to map for analytics purpose
     */
    public openSelectorToggle(toggleKey: string): void {
        this.store.dispatch(openSelectorToggle({ toggleKey }));
    }

    public toggleZoom(data: ToggleZoomEvent): void {
        this.store.dispatch(toggleZoom(data));
    }

    public pageView(product: Product): void {
        this.store.dispatch(sendPageViewAnalytics({ product }));
    }

    public goBack(): void {
        this.store.dispatch(goBack());
    }

    public fetchCrossSellProduct(): void {
        this.store.dispatch(fetchCrossSellProduct());
    }

    public updateProductHeadTag(
        product: Product,
        productReference: string,
    ): void {
        this.store.dispatch(
            updateProductHeadTag({
                product,
                productReference,
            }),
        );
    }

    public selectVariant(
        variantType: ProductVariantType,
        variantItem: AbstractProductVariant,
        position: number = 0,
    ): void {
        this.store.dispatch(
            selectVariant({
                variantType,
                variantItem,
                position,
            }),
        );
    }

    public resetVariant(position: number): void {
        this.store.dispatch(resetVariant({ position }));
    }

    public selectedVariant = (
        position: number,
    ): Observable<SelectedVariantVariantsState> =>
        this.store.select(selectSelectedVariant(position));

    public resetSizeSelectedVariants(): void {
        this.store.dispatch(resetSizeSelectedVariants());
    }

    public showMoreVariant(data: {
        id: string;
        isExpanded: boolean;
        sku?: string;
    }): void {
        this.store.dispatch(showMoreVariant({ data }));
    }

    public fetchProduct(sku: string): void {
        this.store.dispatch(fetchProduct({ sku }));
    }

    public cleanProductState(): void {
        this.store.dispatch(cleanProductState());
    }

    public defaultImageGalleryPosition(): void {
        this.store.dispatch(defaultImageGalleryPosition());
    }

    public resetVariantSelectedBoxType(): void {
        this.store.dispatch(resetVariantSelectedBoxType());
    }

    public showMoreVariantsRequested(
        topLayerVariants?: TopLayerVariants,
    ): void {
        this.store.dispatch(
            showMoreVariantsRequested({ variants: topLayerVariants }),
        );
    }
}
